import type { DocumentContext } from '../domain/DocumentContext';

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

/**
 * The editor canvas with Sample data off (`previewValueBinding: 'unbound'`) keeps every dynamic
 * value as a token chip (LLAMS-610 audit D12) - never resolves it against the empty context into a
 * blank. The export surface (the PDF's HTML twin) always resolves.
 */
export function keepsUnboundTokens(context: Pick<DocumentContext, 'previewValueBinding'>, surface: 'editor' | 'export'): boolean {
  return surface !== 'export' && context.previewValueBinding === 'unbound';
}

/** Inline chip style, one place: `unboundTokenDisplay.tsx` (field kinds) and the prose HTML below share it. */
export const UNBOUND_TOKEN_CHIP_CSS = {
  backgroundColor: 'var(--accent-orange-50)',
  color: 'var(--text-body)',
  padding: '0 var(--size-4)',
  borderRadius: 'var(--border-radius-50)',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  letterSpacing: 'normal'
} as const;

const toCssText = (css: Record<string, string>): string =>
  Object.entries(css)
    .map(([prop, value]) => `${prop.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}:${value}`)
    .join(';');

/**
 * Wraps each `{{token}}` in the TEXT of already-sanitised prose HTML in a chip, exactly as the
 * field kinds draw one. Only text nodes are touched (an `href` that happens to hold a token is not),
 * and the chip is built with DOM APIs from a `[\w.]+` path, so nothing here can introduce markup.
 */
export function chipUnboundTokensInHtml(safeHtml: string): string {
  if (!safeHtml.includes('{{') || typeof document === 'undefined') return safeHtml;

  const template = document.createElement('template');
  template.innerHTML = safeHtml;
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) textNodes.push(node as Text);

  const chipStyle = toCssText(UNBOUND_TOKEN_CHIP_CSS);
  for (const node of textNodes) {
    const text = node.nodeValue ?? '';
    const matches = [...text.matchAll(TOKEN_RE)];
    if (matches.length === 0) continue;

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    for (const match of matches) {
      const start = match.index ?? 0;
      if (start > cursor) fragment.append(text.slice(cursor, start));
      const chip = document.createElement('span');
      chip.setAttribute('data-variable-token', match[1] ?? '');
      chip.setAttribute('style', chipStyle);
      chip.textContent = `{{${match[1] ?? ''}}}`;
      fragment.append(chip);
      cursor = start + match[0].length;
    }
    if (cursor < text.length) fragment.append(text.slice(cursor));
    node.replaceWith(fragment);
  }
  return template.innerHTML;
}
